/**
 * SuiteComboBox — autocomplete input for the (soft-deprecated) suite field.
 *
 * Fetches existing suite values from GET /api/test-cases/meta.
 * Single-value; free-text entry is allowed (just type and blur/Enter).
 */

import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { API_BASE_URL } from '../../../config/api';

const API_URL = API_BASE_URL;

interface SuiteComboBoxProps {
    value:     string;
    onChange:  (suite: string) => void;
    projectId: string | null;
}

export function SuiteComboBox({ value, onChange, projectId }: SuiteComboBoxProps) {
    const { token }                        = useAuth();
    const [inputValue,   setInputValue]    = useState(value);
    const [suggestions,  setSuggestions]   = useState<string[]>([]);
    const [allSuites,    setAllSuites]     = useState<string[]>([]);
    const [dropdownOpen, setDropdownOpen]  = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Sync controlled value → input
    useEffect(() => { setInputValue(value); }, [value]);

    // Fetch suites from meta endpoint
    useEffect(() => {
        if (!token || !projectId) return;
        axios
            .get(`${API_URL}/api/test-cases/meta`, {
                params: { projectId },
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(({ data }) => {
                if (data.success && Array.isArray(data.data?.suites)) {
                    setAllSuites(data.data.suites as string[]);
                }
            })
            .catch(() => { /* silent */ });
    }, [token, projectId]);

    useEffect(() => {
        const q = inputValue.trim().toLowerCase();
        setSuggestions(allSuites.filter((s) => !q || s.toLowerCase().includes(q)));
    }, [inputValue, allSuites]);

    // Close dropdown on outside click
    useEffect(() => {
        if (!dropdownOpen) return;
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setDropdownOpen(false);
                onChange(inputValue.trim());
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [dropdownOpen, inputValue, onChange]);

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter') { e.preventDefault(); onChange(inputValue.trim()); setDropdownOpen(false); }
        if (e.key === 'Escape') setDropdownOpen(false);
    }

    const INPUT_CLASS =
        'w-full px-3 py-2 text-sm border border-slate-200 dark:border-gh-border-dark rounded-lg ' +
        'bg-white dark:bg-gh-bg-dark text-slate-800 dark:text-slate-200 ' +
        'placeholder-slate-400 dark:placeholder-slate-500 ' +
        'focus:outline-none focus:ring-2 focus:ring-blue-500 transition';

    return (
        <div ref={containerRef} data-testid="suite-combobox" className="relative">
            <input
                type="text"
                aria-label="Test suite"
                data-testid="suite-combobox-input"
                value={inputValue}
                onChange={(e) => { setInputValue(e.target.value); onChange(e.target.value); setDropdownOpen(true); }}
                onFocus={() => setDropdownOpen(true)}
                onKeyDown={handleKeyDown}
                placeholder="e.g. Login"
                className={INPUT_CLASS}
            />

            {dropdownOpen && suggestions.length > 0 && (
                <div className="absolute top-full mt-1 left-0 right-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-gh-border-dark rounded-lg shadow-lg z-50 max-h-36 overflow-y-auto">
                    {suggestions.map((s) => (
                        <button
                            key={s}
                            type="button"
                            data-testid={`suite-combobox-suggestion-${s.toLowerCase().replace(/\s+/g, '-')}`}
                            onMouseDown={(e) => { e.preventDefault(); setInputValue(s); onChange(s); setDropdownOpen(false); }}
                            className="w-full text-left px-3 py-1.5 text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        >
                            {s}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
